// Claude Code branch — CLI setup (overhaul §6.2, panel 02·Claude Code). Renders the two
// install commands with copy buttons and polls readiness; advances the moment
// `terum-capture setup` mints an api key (has_api_key), detectable before any captured
// session. No extension. The OS tabs + command rows keep their existing markup (no .wiz
// equivalent); only the panel shell is reskinned.

import { useEffect, useRef, useState } from 'react';
import { useOnboardingReadiness } from '../../hooks/useOnboardingReadiness';
import { OS_TABS, CLAUDE_CODE_COMMANDS, detectOS, type OS } from '../../lib/claude-code-install';

function CommandRow({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    });
  };
  return (
    <div className="cmd">
      <code>{command}</code>
      <button type="button" className={`cmd-copy${copied ? ' copied' : ''}`} onClick={copy}>
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

export function ClaudeCodeStep({ onConnected, onBack }: { onConnected: () => void; onBack: () => void }) {
  const { data } = useOnboardingReadiness({ pollMs: 4000 });
  const fired = useRef(false);
  const [os, setOs] = useState<OS>(detectOS);

  useEffect(() => {
    if (data?.hasApiKey && !fired.current) {
      fired.current = true;
      onConnected();
    }
  }, [data?.hasApiKey, onConnected]);

  return (
    <>
      <div className="crumb">STEP 02 · CLAUDE CODE</div>
      <div className="wh">Connect <span className="em">Claude Code</span>.</div>
      <p className="wp">
        A tiny CLI hook captures your coding sessions automatically. Nothing runs in the
        browser.
      </p>
      <div className="os-tabs" role="tablist" aria-label="Operating system" style={{ marginTop: 22 }}>
        {OS_TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={os === key}
            className={`os-tab${os === key ? ' on' : ''}`}
            onClick={() => setOs(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="step-k">1 · Install</div>
      <CommandRow command={CLAUDE_CODE_COMMANDS[os].install} />
      <div className="step-k">2 · Authenticate &amp; install the hook</div>
      <CommandRow command={CLAUDE_CODE_COMMANDS[os].auth} />
      <p className="section-note" style={{ marginTop: 14 }}>
        Advances the moment <b>setup</b> mints your API key - no session needed.
      </p>
      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>← Back to sources</button>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--inkS)' }}>
          <span className="dot acc pulse" /> Waiting for setup to finish…
        </span>
        <span className="prog">Step 2 · Claude Code</span>
      </div>
    </>
  );
}
